// 3rd Party
import React, { FC, useState } from 'react';
import { connect } from 'react-redux';

// Components
import { FormattedMessageV2 } from '@/views/components/i18n';

// Types
import User from '@/types/user';
import { ReduxState } from '@/redux/modules/types';

// Utils
import classNames from 'classnames';
import { formatMessage } from '@/utils/i18n';
import {
  logDismissUnlockAIStatementReaderFromMarketingBanner,
  logDisplayAIStatementReaderMarketingBanner,
  logUnlockAIStatementReaderFromMarketingBanner,
} from '@/services/usage-log';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { PrimaryLink } from '../buttons';

type StateProps = {
  user?: User | null;
};

type Props = StateProps & {
  bannerCookieKey: string;
  bannerCookieLength: number;
  bannerImage: string;
  i18nBasePath: string;
  permissionKey: (typeof PERMISSIONS)[keyof typeof PERMISSIONS];
  // TODO: Localize in https://capintel.atlassian.net/browse/CONN-8059
  link: string;
  className?: string;
};

const MarketingBanner: FC<Props> = ({ user, bannerCookieKey, bannerCookieLength, bannerImage, i18nBasePath, permissionKey, link, className }) => {
  const hasBannerPermission = userHasPermission(user, permissionKey);
  const [isDismissed, setIsDismissed] = useState(document.cookie.includes(bannerCookieKey));

  const clickDismissed = () => {
    setIsDismissed(true);
    document.cookie = `${bannerCookieKey}=true; path=/; max-age=${bannerCookieLength};`;
    logDismissUnlockAIStatementReaderFromMarketingBanner();
  };

  if (isDismissed || !hasBannerPermission) {
    return null;
  }
  logDisplayAIStatementReaderMarketingBanner();

  return (
    <div className={classNames('marketing-banner', className)} data-testid="marketing-banner">
      <div className="banner-content">
        <img className="banner-image" alt={formatMessage(`${i18nBasePath}.Alt`)} src={bannerImage} />
        <div className="banner-text">
          <div className="banner-title">
            <FormattedMessageV2 toLocalize={`${i18nBasePath}.Title`} />
          </div>
          <div className="banner-description">
            <FormattedMessageV2 toLocalize={`${i18nBasePath}.Description`} />
          </div>
          <div className="banner-link">
            <a href={link} onClick={logUnlockAIStatementReaderFromMarketingBanner} target="_blank" rel="noopener noreferrer">
              <FormattedMessageV2 toLocalize={`${i18nBasePath}.Link`} />
            </a>
          </div>
        </div>
      </div>
      <PrimaryLink noBorder noMargin onClick={() => clickDismissed()}>
        <FormattedMessageV2 toLocalize={`${i18nBasePath}.Dismiss`} />
      </PrimaryLink>
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  user: state.auth.user,
});

export default connect(mapStateToProps)(MarketingBanner);
